import { DvToolbarActionEvent } from '@devinco/ui/grid';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import { environment } from '@env/environment';
import { GridApi, IRowNode, ServerSideTransaction } from 'ag-grid-community';
import {
  BehaviorSubject,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { GridContext } from '../interfaces/grid-context';
import { DialogService } from '@core/services/dialog.service';
import { ColumnType } from '../grid-options/column-types';
import { DvDialogComponent } from '@devinco/ui/dds';
import { ExcelExportStatus } from '@shared/constants/excel-export-status';
import { Entity } from '@shared/constants/entity';
import { SSGridExcelExportPayload } from '../interfaces/grid-excel-export';

@Injectable({ providedIn: 'root' })
export class ToolbarActionsService {
  private readonly restApi = inject(RestApiService);
  private http = inject(HttpClient);
  private dialog = inject(DialogService);

  /** Delete selected row in grid and calls standard delete endpoint.
   * - Ag-grid must have `context.entity` defined
   * - Ag-grid must have a column with `colDef.type: [ColumnType.Id]`
   */
  deleteSelectedRow = (
    event: DvToolbarActionEvent,
    customDialogTextKey?: string,
    /** when entity in url does not match the getRows entity */
    entity?: Entity,
    isSoftDelete = false
  ): void => {
    const { api, context } = event;
    this.openRowDeleteDialog(customDialogTextKey)
      .closed.pipe(
        take(1),
        switchMap(({ source }) => {
          if (source !== 'confirm') return of(false);
          const colDefs = api.getColumns()?.map(c => c.getColDef());
          const { field } =
            colDefs?.find(
              c => c.type?.includes(ColumnType.Id) || c.type?.[0] === 'id'
            ) || {};

          if (!field) return of(false);

          const selectedNodes: IRowNode[] = api.getSelectedNodes();

          if (!selectedNodes.length) {
            throw new Error('No nodes selected while trying to delete a node!');
          }

          const selectedNodeId = api.getSelectedNodes()[0].data[field];

          return isSoftDelete
            ? this.restApi
                .upsert(entity || context.entity!, selectedNodeId, {
                  ShowRecord: false,
                })
                .pipe(map(() => true))
            : this.restApi
                .delete(entity || context.entity!, selectedNodeId)
                .pipe(map(() => true));
        }),
        take(1)
      )
      .subscribe(resp => {
        if (resp) {
          this.selectRowAfterDelete(api);
        }
      });
  };

  openRowDeleteDialog(
    customDialogTextKey?: string
  ): DvDialogComponent<unknown, unknown> {
    return this.dialog.open({
      size: 'md',
      type: 'error',
      title: 'General.Dialog.Delete.Title',
      contentText: customDialogTextKey || 'General.Dialog.Delete.Text',
      confirmText: 'General.Confirm',
      cancelText: 'General.Cancel',
    });
  }

  serverSideExcelExport = (
    entity: string,
    api: GridApi,
    context: GridContext,
    fileName?: string
  ): void => {
    const exportUrl = `${environment.endpoints.restApi}/excel/gridToExcel`;
    const visibleColumns = api
      .getAllGridColumns()
      .filter(col => col.isVisible() === true);
    const fields = visibleColumns.map(col => col.getColDef().field);
    const columnHeaders = visibleColumns.map(col => col.getColDef().headerName);
    const filterModel = { ...api.getFilterModel(), ...context.extraFilter };
    const sortModel = api
      .getColumnState()
      .filter(col => col.sort !== null)
      .map(function (c) {
        return { sort: c.sort, colId: c.colId };
      });

    const excelRequest = {
      fields,
      entity,
      filterModel,
      sortModel,
      columnHeaders,
      quickFilterText: context.quickFilterText,
    } as GetRowsRequest;

    this.downloadExcel(exportUrl, excelRequest, entity, fileName);
  };

  downloadExcel(
    exportUrl: string,
    payload: SSGridExcelExportPayload,
    entity: string,
    fileName?: string
  ): Observable<ExcelExportStatus | undefined> {
    const status = new BehaviorSubject<ExcelExportStatus | undefined>(
      undefined
    );

    this.http
      .post(exportUrl, payload, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        responseType: 'blob',
        observe: 'events',
      })
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tap((resp: any) => {
          if (
            resp?.ok === true &&
            resp?.status === 200 &&
            resp?.body?.size > 20
          ) {
            const blob = new Blob([resp.body], {
              type: 'application/vnd.ms.excel',
            });
            const newFileName = `export-${(fileName || '').toLocaleLowerCase() || (entity || '').toLowerCase() || 'file'}.xlsx`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = newFileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            status.next(ExcelExportStatus.Success);
          } else if (
            resp?.ok === true &&
            resp?.status === 200 &&
            resp?.body?.size <= 2
          ) {
            status.next(ExcelExportStatus.Empty);
          }
        })
      )
      .subscribe();

    return status;
  }

  selectNextNode(api: GridApi, selectedNodes: IRowNode[]): void {
    const rowIndex = selectedNodes[0].rowIndex;
    const lastIndex = api.getLastDisplayedRowIndex();
    const nextIndex = lastIndex > rowIndex! ? rowIndex! + 1 : lastIndex - 1;
    api.getDisplayedRowAtIndex(nextIndex)?.setSelected(true);
  }

  selectRowAfterDelete(api: GridApi): void {
    const selectedNodes = api.getSelectedNodes();
    const { data, rowIndex } = api.getSelectedNodes()[0];

    const rowModelType = api.getGridOption('rowModelType');
    if (rowModelType === 'clientSide') {
      api.applyTransaction({ remove: [data!] });
      const lastIndex = api.getLastDisplayedRowIndex();
      const nextIndex = lastIndex > rowIndex! ? rowIndex! : lastIndex;
      api.getDisplayedRowAtIndex(nextIndex)?.setSelected(true, true);
    } else if (rowModelType === 'serverSide') {
      this.selectNextNode(api, selectedNodes);
      this.removeServerSideRow(api, selectedNodes);
    }
  }

  private removeServerSideRow(api: GridApi, selectedNodes: IRowNode[]): void {
    const transaction: ServerSideTransaction = {
      remove: [selectedNodes[0].data],
    };
    api.applyServerSideTransaction(transaction);
  }
}
